/** How the shop presents itself, and its menu QR code. */
const shop = {
  en: {
    hours: {
      title: "Opening hours",
      hint: 'Shown on your menu so customers can see whether you are open, and when you next open.',
      closed: "Closed",
      opensAt: "Opens",
      closesAt: "Closes",
      timezone: "Timezone",
      timezoneHint: "Times are read in this timezone, wherever the customer is.",
      copyToAll: "Copy Monday to all days",
      saveFailed: "Could not save your opening hours.",
      save: "Save opening hours",
      overnight: "Closes after midnight",
      forbidden: "Only people allowed to edit the shop can change this.",
      days: {
        mon: "Monday",
        tue: "Tuesday",
        wed: "Wednesday",
        thu: "Thursday",
        fri: "Friday",
        sat: "Saturday",
        sun: "Sunday",
      },
    },
    warehouse: {
      title: "Warehouse",
      hint: "Some stock may live somewhere other than the shop. The register only ever sells what is on the shop's shelf.",
      label: "Keep stock in a warehouse",
      on: "Products have a warehouse count, deliveries can go there, and you can move stock between the two.",
      off: "Off: all your stock is in the shop. Nothing about a warehouse is shown.",
      saveFailed: "Could not change that setting.",
    },
    menuOptions: {
      title: "What your menu shows",
      hint: "Changes apply to your live menu straight away.",
      saveFailed: "Could not change that setting.",
      menu_show_prices: {
        label: "Show prices",
        on: "Customers see what everything costs.",
        off: "Browse only — no prices anywhere on your menu, offers included.",
      },
      preorders_enabled: {
        label: "Accept pre-orders",
        on: "Customers can reserve products to collect later.",
        off: "Your menu has no basket and nobody can reserve.",
      },
      show_sister_shops: {
        label: "List my other shops",
        on: "Your menu lists your other shops that have this on too, nearest first.",
        off: "Your menu shows this shop only.",
      },
      menu_show_doses: {
        label: "Say which liquids are sold by the dose",
        on: "Liquids sold by the dose show “also by the dose” and its price.",
        off: "The menu does not mention doses.",
      },
    },
    title: "Your shop",
    subtitle: "What your customers see on your menu and on their receipts.",
    loadFailed: "Could not load your shop.",
    saveFailed: "Could not save your changes.",
    selectedLogo: "Selected: {name}",
    changeLogo: "Change logo",
    name: "Shop name",
    phone: "Phone",
    email: "Email",
    address: "Address",
    currency: "Currency",
    vat: "VAT (%)",
    vatHint:
      "Leave at 0 until you are taxed — nothing about VAT appears anywhere while it is zero.",
    menuCode: "Your menu code",
    menuCodeHint:
      "Print this and put it on your counter. Customers scan it to see everything you have in stock.",
    scanToSee: "Scan to see our menu",
    qrAlt: "Menu QR code",
    print: "🖨 Print counter card",
    download: "Download image",
    copyLink: "Copy link",
    copied: "Menu link copied.",
    slugLabel: "Your shop address is",
    editLink: "Edit link",
    linkHintFirstTime:
      "Pick your own link before you print your QR code — it can only use lowercase letters, numbers and hyphens.",
    linkInvalidFormat:
      "Links can only use lowercase letters, numbers and single hyphens, 3-64 characters long.",
    linkTaken: "This link is already taken — please choose another one.",
    linkSaved: "Your shop's link has been changed.",
    linkWarningTitle: "Changing your link again?",
    linkWarningBody:
      "Your QR code will need to be printed again, and every customer who saved the old link will lose access to your menu until they scan the new one.",
    linkWarningContinue: "I understand, continue",
    linkFinalWarningTitle: "Are you absolutely sure?",
    linkFinalWarningBody:
      "This cannot be undone by itself — you would need to change it again and reprint once more. Confirm the new link is exactly what you want.",
    linkFinalConfirm: "Yes, change my link",
  },

  fr: {
    hours: {
      title: "Horaires d'ouverture",
      hint: 'Affichés sur votre menu pour que les clients sachent si vous êtes ouvert et quand vous rouvrez.',
      closed: "Fermé",
      opensAt: "Ouvre",
      closesAt: "Ferme",
      timezone: "Fuseau horaire",
      timezoneHint: "Les heures sont lues dans ce fuseau, où que soit le client.",
      copyToAll: "Copier lundi sur tous les jours",
      saveFailed: "Impossible d'enregistrer vos horaires.",
      save: "Enregistrer les horaires",
      overnight: "Ferme après minuit",
      forbidden: "Seules les personnes autorisées à modifier la boutique peuvent le changer.",
      days: {
        mon: "Lundi",
        tue: "Mardi",
        wed: "Mercredi",
        thu: "Jeudi",
        fri: "Vendredi",
        sat: "Samedi",
        sun: "Dimanche",
      },
    },
    warehouse: {
      title: "Entrepôt",
      hint: "Une partie du stock peut se trouver ailleurs qu'en boutique. La caisse ne vend que ce qui est sur les étagères de la boutique.",
      label: "Garder du stock dans un entrepôt",
      on: "Les produits ont un stock d'entrepôt, les livraisons peuvent y aller, et vous déplacez le stock entre les deux.",
      off: "Désactivé : tout votre stock est en boutique. Rien sur un entrepôt n'est affiché.",
      saveFailed: "Impossible de modifier ce réglage.",
    },
    menuOptions: {
      title: "Ce que montre votre menu",
      hint: "Les changements s'appliquent immédiatement à votre menu en ligne.",
      saveFailed: "Impossible de modifier ce réglage.",
      menu_show_prices: {
        label: "Afficher les prix",
        on: "Les clients voient le prix de chaque article.",
        off: "Consultation seule : aucun prix sur votre menu, offres comprises.",
      },
      preorders_enabled: {
        label: "Accepter les précommandes",
        on: "Les clients peuvent réserver des produits à retirer plus tard.",
        off: "Votre menu n'a pas de panier et personne ne peut réserver.",
      },
      show_sister_shops: {
        label: "Afficher mes autres boutiques",
        on: "Votre menu affiche vos autres boutiques qui l'ont activé aussi, la plus proche d'abord.",
        off: "Votre menu n'affiche que cette boutique.",
      },
      menu_show_doses: {
        label: "Indiquer les liquides vendus à la dose",
        on: "Les liquides vendus à la dose affichent « aussi à la dose » et son prix.",
        off: "Le menu ne parle pas des doses.",
      },
    },
    title: "Votre boutique",
    subtitle: "Ce que vos clients voient sur votre menu et sur leurs reçus.",
    loadFailed: "Impossible de charger votre boutique.",
    saveFailed: "Impossible d'enregistrer vos modifications.",
    selectedLogo: "Sélectionné : {name}",
    changeLogo: "Changer le logo",
    name: "Nom de la boutique",
    phone: "Téléphone",
    email: "E-mail",
    address: "Adresse",
    currency: "Devise",
    vat: "TVA (%)",
    vatHint:
      "Laissez 0 tant que vous n'êtes pas assujetti — rien concernant la TVA n'apparaît tant qu'elle est à zéro.",
    menuCode: "Le code de votre menu",
    menuCodeHint:
      "Imprimez-le et posez-le sur votre comptoir. Les clients le scannent pour voir tout ce que vous avez en stock.",
    scanToSee: "Scannez pour voir notre menu",
    qrAlt: "QR code du menu",
    print: "🖨 Imprimer la carte comptoir",
    download: "Télécharger l'image",
    copyLink: "Copier le lien",
    copied: "Lien du menu copié.",
    slugLabel: "L'adresse de votre boutique est",
    editLink: "Modifier le lien",
    linkHintFirstTime:
      "Choisissez votre propre lien avant d'imprimer votre QR code — uniquement des lettres minuscules, des chiffres et des tirets.",
    linkInvalidFormat:
      "Le lien ne peut contenir que des lettres minuscules, des chiffres et des tirets simples, 3 à 64 caractères.",
    linkTaken: "Ce lien est déjà pris — veuillez en choisir un autre.",
    linkSaved: "Le lien de votre boutique a été modifié.",
    linkWarningTitle: "Modifier à nouveau votre lien ?",
    linkWarningBody:
      "Votre QR code devra être réimprimé, et chaque client ayant enregistré l'ancien lien perdra l'accès à votre menu jusqu'à ce qu'il scanne le nouveau.",
    linkWarningContinue: "Je comprends, continuer",
    linkFinalWarningTitle: "Êtes-vous absolument sûr ?",
    linkFinalWarningBody:
      "Cela ne peut pas être annulé automatiquement — il faudrait le changer à nouveau et réimprimer une fois de plus. Vérifiez que le nouveau lien est exactement ce que vous voulez.",
    linkFinalConfirm: "Oui, modifier mon lien",
  },

  ar: {
    hours: {
      title: "ساعات العمل",
      hint: 'تظهر في قائمتك ليعرف الزبائن إن كنت مفتوحاً ومتى تفتح مجدداً.',
      closed: "مغلق",
      opensAt: "يفتح",
      closesAt: "يغلق",
      timezone: "المنطقة الزمنية",
      timezoneHint: "تُقرأ الأوقات وفق هذه المنطقة الزمنية أينما كان الزبون.",
      copyToAll: "نسخ الاثنين إلى كل الأيام",
      saveFailed: "تعذّر حفظ ساعات العمل.",
      save: "حفظ ساعات العمل",
      overnight: "يغلق بعد منتصف الليل",
      forbidden: "لا يستطيع تغيير هذا إلا من يملك صلاحية تعديل المتجر.",
      days: {
        mon: "الاثنين",
        tue: "الثلاثاء",
        wed: "الأربعاء",
        thu: "الخميس",
        fri: "الجمعة",
        sat: "السبت",
        sun: "الأحد",
      },
    },
    warehouse: {
      title: "المخزن",
      hint: "قد يكون جزء من المخزون في مكان غير المتجر. والصندوق لا يبيع إلا ما على رفوف المتجر.",
      label: "الاحتفاظ بمخزون في مخزن",
      on: "للمنتجات عدّاد مخزن، ويمكن توجيه التوريدات إليه، وتنقل المخزون بينه وبين المتجر.",
      off: "مُعطّل: كل مخزونك في المتجر ولا يظهر شيء عن مخزن.",
      saveFailed: "تعذّر تغيير هذا الإعداد.",
    },
    menuOptions: {
      title: "ما تعرضه قائمتك",
      hint: "تُطبَّق التغييرات على قائمتك مباشرةً.",
      saveFailed: "تعذّر تغيير هذا الإعداد.",
      menu_show_prices: {
        label: "إظهار الأسعار",
        on: "يرى الزبائن سعر كل منتج.",
        off: "للتصفح فقط — لا أسعار في قائمتك، بما في ذلك العروض.",
      },
      preorders_enabled: {
        label: "قبول الطلبات المسبقة",
        on: "يمكن للزبائن حجز منتجات لاستلامها لاحقاً.",
        off: "لا توجد سلة في قائمتك ولا يمكن لأحد الحجز.",
      },
      show_sister_shops: {
        label: "اعرض متاجري الأخرى",
        on: "تعرض قائمتك متاجرك الأخرى المفعّل فيها هذا الخيار أيضًا، الأقرب أولًا.",
        off: "تعرض قائمتك هذا المتجر فقط.",
      },
      menu_show_doses: {
        label: "اذكر السوائل التي تُباع بالجرعة",
        on: "تعرض السوائل التي تُباع بالجرعة «متوفر أيضًا بالجرعة» وسعرها.",
        off: "لا تذكر القائمة الجرعات.",
      },
    },
    title: "متجرك",
    subtitle: "ما يراه زبائنك في قائمتك وعلى إيصالاتهم.",
    loadFailed: "تعذّر تحميل متجرك.",
    saveFailed: "تعذّر حفظ تغييراتك.",
    selectedLogo: "تم اختيار: {name}",
    changeLogo: "تغيير الشعار",
    name: "اسم المتجر",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    address: "العنوان",
    currency: "العملة",
    vat: "الأداء على القيمة المضافة (%)",
    vatHint:
      "اتركها 0 حتى تخضع للضريبة — لن يظهر أي شيء يخص الضريبة ما دامت صفراً.",
    menuCode: "رمز قائمتك",
    menuCodeHint:
      "اطبعه وضعه على الصندوق. يمسحه الزبائن ليروا كل ما لديك في المخزون.",
    scanToSee: "امسح لرؤية قائمتنا",
    qrAlt: "رمز QR للقائمة",
    print: "🖨 طباعة بطاقة الصندوق",
    download: "تنزيل الصورة",
    copyLink: "نسخ الرابط",
    copied: "تم نسخ رابط القائمة.",
    slugLabel: "عنوان متجرك هو",
    editLink: "تعديل الرابط",
    linkHintFirstTime:
      "اختر رابطك الخاص قبل طباعة رمز QR — يمكن استخدام حروف صغيرة وأرقام وشرطات فقط.",
    linkInvalidFormat:
      "يمكن للرابط أن يحتوي فقط على حروف صغيرة وأرقام وشرطات مفردة، بطول 3 إلى 64 حرفاً.",
    linkTaken: "هذا الرابط مُستخدم من قبل — يرجى اختيار رابط آخر.",
    linkSaved: "تم تغيير رابط متجرك.",
    linkWarningTitle: "تغيير رابطك مرة أخرى؟",
    linkWarningBody:
      "سيحتاج رمز QR إلى إعادة الطباعة، وسيفقد كل زبون حفظ الرابط القديم الوصول إلى قائمتك حتى يمسح الرابط الجديد.",
    linkWarningContinue: "أفهم ذلك، استمرار",
    linkFinalWarningTitle: "هل أنت متأكد تماماً؟",
    linkFinalWarningBody:
      "لا يمكن التراجع عن هذا تلقائياً — سيتوجب عليك تغييره مرة أخرى وإعادة الطباعة مجدداً. تأكد أن الرابط الجديد هو ما تريده بالضبط.",
    linkFinalConfirm: "نعم، غيّر رابطي",
  },
};

export default shop;
